import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormGroup, FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { ChatFireService } from '../../gen2ai/chatfire.service';
import { Conversation, Message } from '../../models/chat.model';

export interface HistoryRow {
  id: string;                      
  conversationId: string;
  conversationTitle: string;
  role: 'user' | 'assistant' | 'system';   
  modelId?: string;
  modelDisplayName?: string;
  content: string;
  timestamp: Date;
  tokens?: number;
  hasImage: boolean;
  image?: string;
}

@Component({
  selector: 'app-history-table',
  templateUrl: './history-table.component.html',
  styleUrls: ['./history-table.component.scss'],
  providers: [DatePipe]
})
export class HistoryTableComponent implements OnInit, OnDestroy {

  filtersForm: FormGroup;
  filtersVisible = true;
  toggleFiltersLabel = 'Hide filters';

  displayedColumns: string[] = [
    'date',
    'role',
    'model',
    'content',
    'conversationId',
    'conversation'
  ];
  dataSource = new MatTableDataSource<HistoryRow>([]);
  originalRows: HistoryRow[] = [];

  availableModels: Array<{ id: string; name: string }> = [
    { id: 'chatgpt54', name: 'ChatGPT 5.4' },
    { id: 'minichatgpt54', name: 'ChatGPT Mini' },
    { id: 'codestralto', name: 'Codestral' },
    { id: 'haiku45', name: 'Claude Haiku 4.5' },
    { id: 'cohere', name: 'Cohere R+' },
    { id: 'deepseek', name: 'DeepSeek R1' },
    { id: 'deepv3', name: 'DeepSeek V3.2' },
    { id: 'gemini', name: 'Gemini Pro' },
    { id: 'maverick', name: 'Llama 4' },
    { id: 'scout', name: 'Meta Scout' },
    { id: 'novalite', name: 'Nova Lite' },
    { id: 'opus46', name: 'Claude Opus 4.6' },
    { id: 'opus47', name: 'Claude Opus 4.7' },
    { id: 'kimik25', name: 'Kimi 2.5' },
    { id: 'palmx', name: 'Palmyra X5' },
    { id: 'pixtralimage', name: 'Pixtral' },
    { id: 'sonnet45', name: 'Sonnet 4.5' }
  ];
  totalMessages = 0;

  private subs = new Subscription();

  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  constructor(
    private chatFire: ChatFireService,
    private datePipe: DatePipe,
    private router: Router
  ) {
    this.filtersForm = new FormGroup({
      search: new FormControl(''),
      roles: new FormControl<string[]>([]),            // ['user','assistant','system']
      model: new FormControl<string>(''),             // modelId
      sinceDate: new FormControl<string>('')          // ngbDatepicker object OR ISO string
    });
  }

  ngOnInit(): void {
    // sorting
    this.dataSource.sortingDataAccessor = (row: HistoryRow, prop: string) => {
      switch (prop) {
        case 'date': return row.timestamp?.getTime?.() ?? 0;
        case 'role':      return row.role?.toLowerCase() ?? '';
        case 'model':     return (row.modelDisplayName || row.modelId || '').toLowerCase();
        case 'content':   return row.content?.toLowerCase() ?? '';
        case 'tokens':    return row.tokens ?? 0;
        case 'conversation': return row.conversationTitle?.toLowerCase() ?? '';
        default:          return (row as any)[prop];
      }
    };
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    // custom filter predicate reading our form state from dataSource.filter (JSON string)
    this.dataSource.filterPredicate = (row: HistoryRow, filterJson: string) => {
      if (!filterJson) return true;
      let f: any;
      try { f = JSON.parse(filterJson); } catch { return true; }

      // search across fields
      const q = (f.search || '').toString().trim().toLowerCase();
      if (q) {
        const hay = [
          row.content,
          row.role,
          row.modelDisplayName,
          row.modelId,
          row.conversationTitle,
          row.conversationId
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }

      // roles
      const roles: string[] = f.roles || [];
      if (roles.length && !roles.includes(row.role)) return false;

      // model filter
      const model: string = f.model || '';
      if (model && row.modelId !== model) return false;

      // since date (messages on/after)
      const sd = f.sinceDate;
      if (sd) {
        const date = this.parseNgbOrIsoDate(sd);
        if (date && row.timestamp < date) return false;
      }

      return true;
    };

    // keep table in sync with ALL conversations by uid (real-time, not filtered by session)
    this.subs.add(
      this.chatFire.allConversationsByUid$.subscribe((convs: Conversation[]) => {
        const rows = this.flattenToRows(convs);
        this.originalRows = rows;
        this.totalMessages = rows.length;
        this.dataSource.data = rows;

        // re-apply existing filters (triggers filterPredicate)
        this.applyFilters(this.filtersForm.value);
      })
    );

    this.subs.add(
      this.filtersForm.valueChanges.subscribe(v => this.applyFilters(v))
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---- actions ----

  search(value: string): void {
    this.filtersForm.patchValue({ search: value || '' }, { emitEvent: true });
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
    this.toggleFiltersLabel = this.filtersVisible ? 'Hide filters' : 'Show filters';
  }

  clearFilters(): void {
    this.filtersForm.reset({
      search: '',
      roles: [],
      model: '',
      sinceDate: ''
    });
    this.applyFilters(this.filtersForm.value);
  }

  goToConversation(row: HistoryRow): void {
    // Keep internal selection in sync and route from any clicked row cell.
    this.chatFire.selectConversation(row.conversationId);
    this.router.navigate(['/library/search', row.conversationId]);
  }

  // ---- helpers ----

  private applyFilters(formValue: any): void {
    // store the *form state* into dataSource.filter as a JSON string
    this.dataSource.filter = JSON.stringify(formValue || {});
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  private flattenToRows(conversations: Conversation[]): HistoryRow[] {
    const rows: HistoryRow[] = [];
    conversations.forEach(c => {
      const conversationTopic = this.chatFire.getConversationTopicFromMessages(c.messages || []);
      (c.messages || []).forEach((m: Message) => {
        rows.push({
          id: m.id,
          conversationId: c.id,
          conversationTitle: this.truncateConversationTopic(conversationTopic || 'Conversation'),
          role: m.role as 'user' | 'assistant' | 'system',   
          modelId: m.modelId,
          modelDisplayName: m.modelDisplayName,
          content: m.content || '',
          timestamp: m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp),
          tokens: (m as any).tokens, // safe-read
          hasImage: !!(m as any).image,
          image: (m as any).image
        });
      });
    });
    // newest first
    rows.sort((a, b) => (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0));
    return rows;
  }

  private parseNgbOrIsoDate(input: any): Date | null {
    // Handles ngbDatepicker object {year,month,day} or ISO string/date
    if (!input) return null;
    if (typeof input === 'object' && 'year' in input && 'month' in input && 'day' in input) {
      return new Date(input.year, input.month - 1, input.day);
    }
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  truncate(text: string, max = 160): string {
    if (!text) return '';
    return text.length <= max ? text : text.slice(0, max) + '…';
  }

  truncateConversationTopic(topic: string): string {
    const value = String(topic || '').trim();
    if (!value) return 'Conversation';
    return value.length <= 13 ? value : `${value.slice(0, 13)}...`;
  }

  formatDate(timestamp: Date | string | number): string {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return this.datePipe.transform(date, 'MM/dd/yyyy') ?? '';
  }
}
